import { Inject, Injectable, PLATFORM_ID, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { AppConfig } from '../models/appconfig';
import { AppState } from '../models/appstate';

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    private readonly CONFIG_KEY = 'hr-app-config';
    private isInitialized = false;
    
    _config: AppConfig = {
        theme: 'aura-light-blue',
        darkMode: false,
        ripple: true,
        scale: 14,
        tableTheme: 'lara-light-blue'
    };

    state: AppState = {
        configActive: false,
        menuActive: false,
        newsActive: false
    };

    config = signal<AppConfig>(this._config);

    private configUpdate = new Subject<AppConfig>();

    configUpdate$ = this.configUpdate.asObservable();

    constructor(@Inject(PLATFORM_ID) private platformId: any) {
        // Load saved config from localStorage first
        this.loadConfigFromStorage();
        
        effect(() => {
            const config = this.config();
            if (isPlatformBrowser(this.platformId)) {
                if (this.updateStyle(config)) {
                    this.changeTheme();
                    const newTableTheme = !config.darkMode ? config.tableTheme.replace('dark', 'light') : config.tableTheme.replace('light', 'dark');
                    this.replaceTableTheme(newTableTheme);
                }
                this.changeScale(config.scale);
                this.onConfigUpdate();
            }
        });
        
        // Apply loaded config immediately after initialization
        if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
                this.applyCurrentConfig();
                this.isInitialized = true;
            }, 0);
        } else {
            this.isInitialized = true;
        }
    }

    updateStyle(config: AppConfig) {
        return config.theme !== this._config.theme || config.darkMode !== this._config.darkMode || config.tableTheme !== this._config.tableTheme;
    }

    onConfigUpdate() {
        const config = this.config();
        config.tableTheme = !config.darkMode ? config.tableTheme.replace('light', 'dark') : config.tableTheme.replace('dark', 'light');
        this._config = { ...config };
        this.configUpdate.next(this.config());
        // Only save to localStorage if this is not the initial load
        if (this.isInitialized) {
            this.saveConfigToStorage();
        }
    }

    showMenu() {
        this.state.menuActive = true;
    }

    hideMenu() {
        this.state.menuActive = false;
    }

    showConfig() {
        this.state.configActive = true;
    }

    hideConfig() {
        this.state.configActive = false;
    }

    showNews() {
        this.state.newsActive = true;
    }

    hideNews() {
        this.state.newsActive = false;
    }

    changeTheme() {
        const config = this.config();
        const themeLink = <HTMLLinkElement>document.getElementById('theme-link');
        
        if (themeLink) {
            const themeLinkHref = themeLink.getAttribute('href')!;
            const newHref = themeLinkHref
                .split('/')
                .map((el) => {
                    // Replace theme name in the path
                    if (el.includes('aura-') || el.includes('lara-') || el.includes('saga-') || 
                        el.includes('nova-') || el.includes('md-') || el.includes('mdc-') || 
                        el.includes('soho-') || el.includes('viva-') || el.includes('nano-') || 
                        el.includes('rhea-') || el.includes('mira-')) {
                        return config.theme;
                    }
                    return el;
                })
                .join('/');

            // Only update if href actually changes
            if (newHref !== themeLinkHref) {
                this.replaceThemeLink(newHref);
            }
        }
        
        // Save config after theme change (but avoid infinite loop)
        if (config.theme !== this._config.theme) {
            this.saveConfigToStorage();
        }
    }

    replaceThemeLink(href: string) {
        const id = 'theme-link';
        let themeLink = <HTMLLinkElement>document.getElementById(id);
        const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        themeLink.parentNode!.insertBefore(cloneLinkElement, themeLink.nextSibling);
        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    replaceTableTheme(newTheme: string) {
        const elementId = 'home-table-link';
        const linkElement = <HTMLLinkElement>document.getElementById(elementId);
        const tableThemeTokens = linkElement?.getAttribute('href').split('/') || null;
        const currentTableTheme = tableThemeTokens ? tableThemeTokens[tableThemeTokens.length - 2] : null;
        if (currentTableTheme !== newTheme && tableThemeTokens) {
            const newThemeUrl = linkElement.getAttribute('href').replace(currentTableTheme, newTheme);

            const cloneLinkElement = <HTMLLinkElement>linkElement.cloneNode(true);

            cloneLinkElement.setAttribute('id', elementId + '-clone');
            cloneLinkElement.setAttribute('href', newThemeUrl);
            cloneLinkElement.addEventListener('load', () => {
                linkElement.remove();
                cloneLinkElement.setAttribute('id', elementId);
            });
            linkElement.parentNode?.insertBefore(cloneLinkElement, linkElement.nextSibling);
        }
    }

    changeScale(value: number) {
        if (isPlatformBrowser(this.platformId)) {
            document.documentElement.style.fontSize = `${value}px`;
        }
    }

    /**
     * Apply current config immediately (used after loading from localStorage)
     */
    private applyCurrentConfig(): void {
        const config = this.config();
        if (isPlatformBrowser(this.platformId)) {
            // Apply theme
            this.changeTheme();
            
            // Apply table theme
            const tableTheme = !config.darkMode ? config.tableTheme.replace('dark', 'light') : config.tableTheme.replace('light', 'dark');
            this.replaceTableTheme(tableTheme);
            
            // Apply scale
            this.changeScale(config.scale);
        }
    }

    /**
     * Load config from localStorage
     */
    private loadConfigFromStorage(): void {
        if (isPlatformBrowser(this.platformId)) {
            try {
                const savedConfig = localStorage.getItem(this.CONFIG_KEY);
                if (savedConfig) {
                    const parsedConfig: AppConfig = JSON.parse(savedConfig);
                    // Merge with default config to ensure all properties exist
                    this._config = { ...this._config, ...parsedConfig };
                    this.config.set(this._config);
                    console.log('Loaded config from localStorage:', this._config);
                }
            } catch (error) {
                console.warn('Failed to load app config from localStorage:', error);
            }
        }
    }

    /**
     * Save current config to localStorage
     */
    private saveConfigToStorage(): void {
        if (isPlatformBrowser(this.platformId)) {
            try {
                const configToSave = this.config();
                localStorage.setItem(this.CONFIG_KEY, JSON.stringify(configToSave));
            } catch (error) {
                console.warn('Failed to save app config to localStorage:', error);
            }
        }
    }

    /**
     * Update theme and save config
     */
    public setTheme(theme: string): void {
        const currentConfig = this.config();
        const updatedConfig = { ...currentConfig, theme: theme };
        this._config = updatedConfig;
        this.config.set(updatedConfig);
        this.saveConfigToStorage();
    }

    /**
     * Update dark mode and save config
     */
    public setDarkMode(darkMode: boolean): void {
        const currentConfig = this.config();
        const updatedConfig = { ...currentConfig, darkMode: darkMode };
        this._config = updatedConfig;
        this.config.set(updatedConfig);
        this.saveConfigToStorage();
    }

    /**
     * Update scale and save config
     */
    public setScale(scale: number): void {
        const currentConfig = this.config();
        const updatedConfig = { ...currentConfig, scale: scale };
        this._config = updatedConfig;
        this.config.set(updatedConfig);
        this.changeScale(scale);
        this.saveConfigToStorage();
    }

    /**
     * Update config and save to localStorage
     */
    public updateConfig(newConfig: Partial<AppConfig>): void {
        const currentConfig = this.config();
        const updatedConfig = { ...currentConfig, ...newConfig };
        this._config = updatedConfig;
        this.config.set(updatedConfig);
        this.saveConfigToStorage();
    }

    /**
     * Reset config to default and clear localStorage
     */
    public resetConfig(): void {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(this.CONFIG_KEY);
        }
        
        this._config = {
            theme: 'aura-light-blue',
            darkMode: false,
            ripple: true,
            scale: 14,
            tableTheme: 'lara-light-blue'
        };
        this.config.set(this._config);
    }
}
